import { html, LitElement, PropertyValues, TemplateResult } from 'lit';
import { customElement, property, queryAsync } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { translateFn } from '../../../../utils/translator.helper';
import { GzButtonWebComponent } from '../../../../ui/gzButton';
import { unsafeHTML, UnsafeHTMLDirective } from 'lit/directives/unsafe-html.js';
import { renderIconSet } from '../types/uiIconSet';
import { pivotToInProductTrialsIntelliZone } from '../../../../utils/xdrOverview/intellizone/intellizone.utils';
import { CompanyViewType, GzIzIntegrationStatus } from '../types/GzIzIntegrationStatusTypes';
import { ButtonEvents } from '../../../../ui/gzButton/types';
import { DirectiveResult } from 'lit/directive.js';

@customElement('gz-iz-integration-status')
export class GzIzIntegrationStatusWebComponent extends LitElement {
  @property({ type: String, attribute: true, reflect: true })
  integrationStatus?: GzIzIntegrationStatus;

  @property({ type: Boolean, attribute: true, reflect: true })
  canPivotToInProductTrials = false;

  @property({ type: String, attribute: true, reflect: true })
  companyViewType = CompanyViewType.MY_COMPANY;

  @queryAsync('gz-button')
  private button: GzButtonWebComponent;

  static get styles() {
    return styles;
  }

  protected async updated(_changedProperties: PropertyValues) {
    super.updated(_changedProperties);
    if (_changedProperties.has('canPivotToInProductTrials')) {
      const button = await this.button;
      if (button) {
        button.addEventListener(ButtonEvents.CLICK, () => {
          pivotToInProductTrialsIntelliZone();
        });
      }
    }
  }

  private renderIntegrationStatus() {
    return this.companyViewType === CompanyViewType.MY_COMPANY
      ? this.renderMyCompanyIntegrationStatus()
      : this.renderEditCompanyIntegrationStatus();
  }

  private renderMyCompanyIntegrationStatus(): string | DirectiveResult<typeof UnsafeHTMLDirective> {
    if (this.integrationStatus === GzIzIntegrationStatus.LINKED) {
      return translateFn('Your company has an active integration with IntelliZone.');
    }
    if (!this.canPivotToInProductTrials) {
      return translateFn('Your company does not have an active integration with IntelliZone. Contact your Account Manager to request access.');
    }

    return unsafeHTML(translateFn(
      'Your company does not have an active integration with IntelliZone. To request access, go to the {OPEN_TAG}Products Hub{CLOSE_TAG} page.',
      {
        OPEN_TAG: '<gz-button class=\'ipt-link-button\' type=\'link\' color=\'secondary\' buttonHeight=\'small\'>',
        CLOSE_TAG: '</gz-button>',
      },
    ));
  }

  private renderEditCompanyIntegrationStatus() {
    return this.integrationStatus === GzIzIntegrationStatus.LINKED
      ? translateFn('The company has an active integration with IntelliZone.')
      : translateFn('The company does not have an active integration with IntelliZone.');
  }


  render() {
    const icon = this.integrationStatus === GzIzIntegrationStatus.LINKED ? 'link_company' : 'addlink_company';
    return html`
      ${renderIconSet.createIconSet(icon)}
      <gz-text id="text-link-iz" fontSize="12"> ${this.renderIntegrationStatus()}</gz-text>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-iz-integration-status': GzIzIntegrationStatusWebComponent;
  }
}
